import React from "react";

/**
 * AdvancedScannersGrid - Grid de cards para los escaneos avanzados
 * Diseño inspirado en CAO ERP - Cards con iconos, colores y animaciones
 */
const AdvancedScannersGrid = ({ activeTab, setActiveTab, onClose }) => {
  
  // Categorías de escáneres
  const scannerCategories = [
    {
      title: "Seguridad de APIs y Bots",
      color: "from-purple-600 to-indigo-600",
      scanners: [
        { id: "api-security", name: "Scanner de APIs", icon: "🔌", description: "OWASP API Top 10", color: "bg-purple-500" },
        { id: "ai-bot-security", name: "Scanner de Bots IA", icon: "🤖", description: "Seguridad de Chatbots", color: "bg-indigo-500" },
        { id: "demo-chatbot", name: "Demo Chatbot", icon: "💬", description: "Bot de Prueba Vulnerable", color: "bg-violet-500" },
      ]
    },
    {
      title: "Inteligencia de Amenazas",
      color: "from-red-600 to-orange-600",
      scanners: [
        { id: "dark-web", name: "Dark Web", icon: "🌑", description: "Credenciales Expuestas", color: "bg-gray-800" },
        { id: "brand-impersonation", name: "Brand Impersonation", icon: "🎭", description: "Suplantación de Marca", color: "bg-red-500" },
        { id: "osint", name: "OSINT Scanner", icon: "🔍", description: "Inteligencia de fuentes abiertas", color: "bg-orange-500" },
      ]
    },
    {
      title: "Infraestructura y Red",
      color: "from-blue-600 to-cyan-600",
      scanners: [
        { id: "network-enum", name: "Network Enumeration", icon: "🌐", description: "Mapeo de infraestructura", color: "bg-blue-500" },
        { id: "microsegmentation", name: "Microsegmentación", icon: "🛡️", description: "Detección Ransomware", color: "bg-cyan-500" },
        { id: "advanced-web", name: "Advanced Web Testing", icon: "🕸️", description: "Pruebas de aplicaciones web", color: "bg-sky-500" },
        { id: "web-hygiene", name: "Higiene Web (SSRF/CSRF)", icon: "🧼", description: "Controles CSRF y SSRF", color: "bg-fuchsia-500" },
      ]
    },
    {
      title: "Postura y Compliance",
      color: "from-green-600 to-emerald-600",
      scanners: [
        { id: "security-posture", name: "Postura de Seguridad", icon: "📋", description: "Evaluación de postura", color: "bg-green-500" },
        { id: "ai-attack-readiness", name: "AI Attack Readiness", icon: "⚔️", description: "Preparación ante ataques", color: "bg-emerald-500" },
        { id: "executive-report", name: "Resumen Ejecutivo", icon: "📊", description: "Reportes y Análisis", color: "bg-teal-500" },
      ]
    },
    {
      title: "Sistemas Internos",
      color: "from-amber-600 to-yellow-600",
      scanners: [
        { id: "internal-agent", name: "Agente Interno", icon: "🖥️", description: "Escaneo de sistemas legacy", color: "bg-amber-500" },
        { id: "usb-policy", name: "Control USB", icon: "🔌", description: "Políticas de medios extraíbles", color: "bg-yellow-500" },
      ]
    }
  ];

  const handleScannerClick = (scannerId) => {
    setActiveTab(scannerId);
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header - sticky dentro del contenedor */}
        <div className="sticky top-0 z-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">🔬 Escaneos Avanzados</h2>
              <p className="text-blue-100 mt-1">Seleccione el tipo de análisis que desea realizar</p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {scannerCategories.map((category, catIndex) => (
            <div key={catIndex}>
              {/* Category Header */}
              <div className="flex items-center mb-4">
                <div className={`h-1 w-8 rounded bg-gradient-to-r ${category.color} mr-3`}></div>
                <h3 className="text-lg font-semibold text-gray-800">{category.title}</h3>
              </div>
              
              {/* Scanner Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.scanners.map((scanner) => (
                  <button
                    key={scanner.id}
                    onClick={() => handleScannerClick(scanner.id)}
                    className={`group relative p-4 bg-white border-2 rounded-xl transition-all duration-200 text-left
                      ${activeTab === scanner.id 
                        ? "border-blue-500 ring-2 ring-blue-200 shadow-lg" 
                        : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                      }`}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-12 h-12 ${scanner.color} rounded-xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform`}>
                        {scanner.icon}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {scanner.name}
                        </h4>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {scanner.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Active indicator */}
                    {activeTab === scanner.id && (
                      <div className="absolute top-2 right-2">
                        <span className="flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                        </span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer - sticky al fondo */}
        <div className="sticky bottom-0 z-20 bg-gray-50 px-6 py-4 rounded-b-2xl border-t flex-shrink-0">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              💡 Los escaneos avanzados requieren dominios autorizados
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-medium transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedScannersGrid;
